'use client';

import { useState, useEffect } from 'react';
import { useTranslation } from '@/hooks/useTranslation';
import { Check, X, Loader2, Eye } from 'lucide-react';
import { aiAssistantApi, type KnowledgeFix, type KnowledgeFixStatus } from '@/lib/api/ai-assistant';
import { PageState } from '@/components/common/feedback/PageState';

const STATUS_COLORS: Record<KnowledgeFixStatus, string> = {
  PENDING: 'bg-yellow-100 text-yellow-700',
  APPROVED: 'bg-green-100 text-green-700',
  REJECTED: 'bg-red-100 text-red-700',
};

/**
 * 知识纠正管理页面
 */
export default function KnowledgePage() {
  const { t } = useTranslation();
  const [fixes, setFixes] = useState<KnowledgeFix[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [filter, setFilter] = useState<KnowledgeFixStatus | 'ALL'>('PENDING');
  const [selectedFix, setSelectedFix] = useState<KnowledgeFix | null>(null);
  const [reviewNote, setReviewNote] = useState('');
  const [isReviewing, setIsReviewing] = useState(false);

  useEffect(() => {
    loadFixes();
  }, [filter]);

  const loadFixes = async () => {
    setIsLoading(true);
    try {
      const params: any = { page: 1, limit: 50 };
      if (filter !== 'ALL') {
        params.status = filter;
      }
      const response: any = await aiAssistantApi.getKnowledgeFixes(params);
      setFixes(response.data.items || []);
    } catch (err) {
      console.error('Failed to load knowledge fixes:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleReview = async (status: 'APPROVED' | 'REJECTED') => {
    if (!selectedFix) return;

    setIsReviewing(true);
    try {
      await aiAssistantApi.reviewKnowledgeFix(selectedFix.id, {
        status,
        reviewNote: reviewNote.trim() || undefined,
      });
      setSelectedFix(null);
      setReviewNote('');
      loadFixes();
    } catch (err) {
      console.error('Failed to review knowledge fix:', err);
    } finally {
      setIsReviewing(false);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleString();
  };

  return (
    <div className="p-6">
      {/* 页面标题 */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">
            {t.aiAssistant.knowledge.title}
          </h1>
          <p className="text-gray-500 mt-1">
            {t.aiAssistant.knowledge.description}
          </p>
        </div>
      </div>

      {/* 过滤器 */}
      <div className="flex gap-2 mb-6">
        {(['PENDING', 'APPROVED', 'REJECTED', 'ALL'] as const).map((status) => (
          <button
            key={status}
            onClick={() => setFilter(status)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              filter === status
                ? 'bg-purple-600 text-white'
                : 'bg-white text-gray-600 border hover:bg-gray-50'
            }`}
          >
            {status === 'ALL'
              ? t.aiAssistant.knowledge.filterAll
              : t.aiAssistant?.knowledge?.statuses?.[status as KnowledgeFixStatus] || status}
          </button>
        ))}
      </div>

      {/* 列表 */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100">
        {isLoading ? (
          <div className="flex items-center justify-center py-16">
            <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
          </div>
        ) : fixes.length === 0 ? (
          <div className="py-12">
            <PageState
              variant="empty"
              title={t.aiAssistant.knowledge.emptyTitle}
              description={t.aiAssistant.knowledge.emptyDesc}
              layout="center"
              className="mx-auto w-full max-w-2xl border-0 bg-transparent"
            />
          </div>
        ) : (
          <div className="divide-y">
            {fixes.map((fix) => (
              <div key={fix.id} className="p-4 hover:bg-gray-50">
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`px-2 py-1 text-xs rounded-full ${STATUS_COLORS[fix.status]}`}>
                        {t.aiAssistant?.knowledge?.statuses?.[fix.status] || fix.status}
                      </span>
                      <span className="text-xs text-gray-400">
                        {formatDate(fix.createdAt)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-900 line-clamp-3">
                      {fix.correctAnswer}
                    </p>
                  </div>
                  <div className="flex items-center gap-2 ml-4">
                    <button
                      onClick={() => {
                        setSelectedFix(fix);
                        setReviewNote('');
                      }}
                      className="p-2 text-gray-500 hover:bg-gray-100 rounded-lg"
                      title={t.aiAssistant.knowledge.view}
                    >
                      <Eye className="w-4 h-4" />
                    </button>
                    {fix.status === 'PENDING' && (
                      <>
                        <button
                          onClick={() => {
                            setSelectedFix(fix);
                            handleReview('APPROVED');
                          }}
                          className="p-2 text-green-500 hover:bg-green-50 rounded-lg"
                          title={t.aiAssistant.knowledge.approve}
                        >
                          <Check className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => {
                            setSelectedFix(fix);
                            handleReview('REJECTED');
                          }}
                          className="p-2 text-red-500 hover:bg-red-50 rounded-lg"
                          title={t.aiAssistant.knowledge.reject}
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 详情弹窗 */}
      {selectedFix && (
        <>
          <div className="fixed inset-0 bg-black/50 z-40" onClick={() => setSelectedFix(null)} />
          <div className="fixed top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 z-50 w-full max-w-lg bg-white rounded-xl shadow-2xl">
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <h3 className="text-lg font-semibold">{t.aiAssistant.knowledge.dialogTitle}</h3>
              <button
                onClick={() => setSelectedFix(null)}
                className="p-1 hover:bg-gray-100 rounded-lg"
              >
                ×
              </button>
            </div>
            <div className="p-6 space-y-4">
              <div>
                <p className="text-sm text-gray-500 mb-1">
                  {t.aiAssistant.knowledge.correctAnswer}
                </p>
                <p className="text-sm bg-gray-50 p-3 rounded-lg whitespace-pre-wrap">
                  {selectedFix.correctAnswer}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <p className="text-sm text-gray-500">{t.aiAssistant.knowledge.status}</p>
                  <span className={`px-2 py-1 text-xs rounded-full ${STATUS_COLORS[selectedFix.status]}`}>
                    {t.aiAssistant.knowledge.statuses[selectedFix.status] || selectedFix.status}
                  </span>
                </div>
                <div>
                  <p className="text-sm text-gray-500">{t.aiAssistant.knowledge.createdAt}</p>
                  <p className="text-sm">{formatDate(selectedFix.createdAt)}</p>
                </div>
              </div>

              {selectedFix.reviewNote && (
                <div>
                  <p className="text-sm text-gray-500 mb-1">
                    {t.aiAssistant.knowledge.reviewNote}
                  </p>
                  <p className="text-sm bg-blue-50 p-3 rounded-lg">{selectedFix.reviewNote}</p>
                </div>
              )}

              {selectedFix.status === 'PENDING' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    {t.aiAssistant.knowledge.reviewNoteOptional}
                  </label>
                  <textarea
                    value={reviewNote}
                    onChange={(e) => setReviewNote(e.target.value)}
                    rows={3}
                    className="w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500/20 focus:border-purple-500"
                    placeholder={t.aiAssistant.knowledge.reviewNotePlaceholder}
                  />
                </div>
              )}
            </div>
            <div className="flex justify-end gap-3 px-6 py-4 border-t bg-gray-50">
              <button
                onClick={() => setSelectedFix(null)}
                className="px-4 py-2 text-gray-700 border rounded-lg hover:bg-gray-100"
              >
                {t.aiAssistant.knowledge.close}
              </button>
              {selectedFix.status === 'PENDING' && (
                <>
                  <button
                    onClick={() => handleReview('REJECTED')}
                    disabled={isReviewing}
                    className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-50"
                  >
                    {t.aiAssistant.knowledge.reject}
                  </button>
                  <button
                    onClick={() => handleReview('APPROVED')}
                    disabled={isReviewing}
                    className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
                  >
                    {isReviewing && <Loader2 className="w-4 h-4 animate-spin" />}
                    {t.aiAssistant.knowledge.approve}
                  </button>
                </>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
